import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    FaUserPlus, FaPaperPlane, FaTrash, FaCoins, FaChalkboardTeacher, 
    FaEdit, FaBookOpen, FaStickyNote, FaSave, FaTimes, FaUnlockAlt, 
    FaCheckSquare, FaUsers, FaSignOutAlt, FaStar, FaClipboardList, FaCheckDouble
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext'; 
import { useNavigation } from '../contexts/NavigationContext'; 
import api from '../api'; 
import PageLayout from '../components/PageLayout'; 
import AddUserModal from '../components/AddUserModal';
import EditUserModal from '../components/EditUserModal';

// --- LOAD REAL DATA ---
let REAL_COURSE_DATA = {};
try {
    const { COURSE_UNITS_DATA } = require('../data/unitsData');
    REAL_COURSE_DATA = COURSE_UNITS_DATA;
} catch (e) {
    console.warn("Could not load unitsData, using fallback list.");
}

// --- DYNAMIC COURSE LIST ---
let ALL_COURSES = [];
if (Object.keys(REAL_COURSE_DATA).length > 0) {
    ALL_COURSES = Object.keys(REAL_COURSE_DATA).map(key => ({
        id: key,
        label: REAL_COURSE_DATA[key].title || key.charAt(0).toUpperCase() + key.slice(1)
    }));
}

const QUESTION_NUMBERS = Array.from({length: 50}, (_, i) => `Question ${i + 1}`);

// --- HELPER: GENERATE LESSON LIST ---
const getLessonList = () => {
    let options = [];
    if (Object.keys(REAL_COURSE_DATA).length > 0) {
        Object.values(REAL_COURSE_DATA).forEach(course => {
            if (course.units) {
                course.units.forEach(unit => {
                    if (unit.lessons) {
                        unit.lessons.forEach(lesson => {
                            options.push(`${course.title}: ${unit.title} - ${lesson.replace('.js', '')}`);
                        });
                    }
                });
            }
        });
    }
    return options;
};

// --- COMPONENT: COURSE & CLASS ACCESS MODAL ---
const CourseAccessModal = ({ student, classes, onClose, onSave }) => {
    // Helper to safely parse database string to array
    const parseCourses = (courseData) => {
        if (Array.isArray(courseData)) return courseData;
        if (!courseData) return [];
        try { 
            // Cleanup quotes if double stringified
            let clean = courseData;
            if (typeof clean === 'string' && clean.startsWith('"') && clean.endsWith('"')) {
                clean = clean.slice(1, -1);
            }
            const parsed = JSON.parse(clean);
            return Array.isArray(parsed) ? parsed : [];
        } 
        catch { return []; }
    };

    // We prioritize reading the old field since that's where data lives
    const initialCourses = student.allowedCourses || student.enrolledCourses;
    const [enrolled, setEnrolled] = useState(parseCourses(initialCourses));
    
    const [assignedClass, setAssignedClass] = useState(student.assignedClass || '');
    const [assignedLesson, setAssignedLesson] = useState(student.assignedLesson || '');
    const [saving, setSaving] = useState(false);
    
    const lessonOptions = getLessonList();

    const toggleCourse = (courseId) => {
        if (enrolled.includes(courseId)) {
            setEnrolled(enrolled.filter(c => c !== courseId));
        } else {
            setEnrolled([...enrolled, courseId]);
        }
    };

    const handleUnlockAll = () => {
        const allIds = ALL_COURSES.map(c => c.id);
        setEnrolled(allIds);
    };

    const handleSave = async () => {
        setSaving(true);
        // FIX: Stringify properly once.
        const coursesString = JSON.stringify(enrolled);
        await onSave(student.id, coursesString, assignedClass, assignedLesson);
        setSaving(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
                <div className="p-5 border-b flex justify-between items-center bg-green-50 rounded-t-2xl">
                    <h3 className="text-xl font-bold text-green-900 flex items-center gap-2">
                        <FaUnlockAlt /> Manage Access: {student.name}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><FaTimes size={24} /></button>
                </div>
                
                <div className="p-6 space-y-6 overflow-y-auto">
                    
                    {/* 1. Assign Class (DROPDOWN) */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-2">
                            <FaUsers /> Assign Class Group
                        </label>
                        <select 
                            value={assignedClass}
                            onChange={(e) => setAssignedClass(e.target.value)}
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none font-bold text-gray-700 bg-white"
                        >
                            <option value="">-- No Class Assigned --</option>
                            {classes && classes.length > 0 ? (
                                classes.map(cls => (
                                    <option key={cls.id} value={cls.name}>
                                        {cls.name} {cls.teacherName ? `(Teacher: ${cls.teacherName})` : ''}
                                    </option>
                                ))
                            ) : (
                                <option disabled>No classes found</option>
                            )}
                        </select>
                    </div>

                    {/* 2. Assign Lesson (DROPDOWN) */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-2">
                            <FaStar className="text-orange-400" /> Assign Next Lesson
                        </label>
                        <select 
                            value={assignedLesson} 
                            onChange={(e) => setAssignedLesson(e.target.value)} 
                            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none font-bold text-gray-700 bg-white"
                        >
                            <option value="">-- No Assignment --</option>
                            {lessonOptions.map((l, idx) => (
                                <option key={idx} value={l}>{l}</option>
                            ))}
                        </select>
                    </div>

                    {/* 3. Assign Courses */}
                    <div>
                        <div className="flex justify-between items-end mb-3">
                            <p className="text-sm text-gray-500 font-bold uppercase tracking-wide">Unlock Courses:</p>
                            <button onClick={handleUnlockAll} className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg font-bold hover:bg-blue-200 flex items-center gap-1 transition-colors">
                                <FaCheckDouble /> Unlock All
                            </button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                            {ALL_COURSES.map(course => (
                                <div key={course.id} onClick={() => toggleCourse(course.id)} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${enrolled.includes(course.id) ? 'border-green-500 bg-white text-green-900 shadow-sm' : 'border-transparent hover:bg-white text-gray-500'}`}>
                                    <div className={`w-5 h-5 rounded flex items-center justify-center border ${enrolled.includes(course.id) ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300'}`}>
                                        {enrolled.includes(course.id) && <FaCheckSquare size={12} />}
                                    </div>
                                    <span className="font-bold text-sm">{course.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2 rounded-lg text-gray-500 font-bold hover:bg-gray-200">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="px-6 py-2 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700 flex items-center gap-2 shadow-lg">
                        {saving ? "Saving..." : <><FaSave /> Save Access</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENT: NOTES MODAL (Standard) ---
const StudentNotesModal = ({ student, onClose, onSave }) => {
    const parseNotes = (noteString) => {
        try { return JSON.parse(noteString) || { lesson: '', question: '', text: '' }; } 
        catch { return { lesson: '', question: '', text: noteString || '' }; }
    };
    const [data, setData] = useState(parseNotes(student.notes));
    const [saving, setSaving] = useState(false);
    const lessonOptions = getLessonList();

    const handleSave = async () => {
        setSaving(true);
        await onSave(student.id, JSON.stringify(data));
        setSaving(false);
        onClose();
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col">
                <div className="p-5 border-b flex justify-between items-center bg-purple-50 rounded-t-2xl">
                    <h3 className="text-xl font-bold text-purple-900 flex items-center gap-2"><FaStickyNote /> Notes: {student.name}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><FaTimes size={24} /></button>
                </div>
                <div className="p-6 space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Current Lesson</label>
                        <select value={data.lesson} onChange={(e) => setData({...data, lesson: e.target.value})} className="w-full p-3 rounded-lg border border-gray-300 font-bold text-gray-700">
                            <option value="">-- Select Lesson --</option>
                            {lessonOptions.map((l, idx) => <option key={idx} value={l}>{l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Stopped At</label>
                        <select value={data.question} onChange={(e) => setData({...data, question: e.target.value})} className="w-full p-3 rounded-lg border border-gray-300 font-bold text-gray-700">
                            <option value="">-- Select Question --</option>
                            <option value="Completed">★ Lesson Completed</option>
                            {QUESTION_NUMBERS.map(q => <option key={q} value={q}>{q}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Observations</label>
                        <textarea rows="4" value={data.text} onChange={(e) => setData({...data, text: e.target.value})} className="w-full p-3 rounded-lg border border-gray-300 resize-none" placeholder="Enter notes..." />
                    </div>
                </div>
                <div className="p-5 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2 rounded-lg text-gray-500 font-bold hover:bg-gray-200">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="px-6 py-2 rounded-lg bg-purple-600 text-white font-bold hover:bg-purple-700 flex items-center gap-2">{saving ? "Saving..." : <><FaSave /> Save</>}</button>
                </div>
            </div>
        </div>
    );
};

// --- CHAT & GENERAL MODAL ---
const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[95vh]">
            <div className="p-5 border-b flex justify-between items-center bg-gray-50 rounded-t-2xl">
                <h3 className="text-xl font-bold text-gray-800">{title}</h3>
                <button onClick={onClose} className="text-gray-400 hover:text-red-500 text-3xl transition-colors">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto">{children}</div>
        </div>
    </div>
);

const DashboardChat = ({ currentUser, allUsers }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const bottomRef = useRef(null);

    const usersList = allUsers || [];

    const chatContacts = usersList.filter(u => u.id !== currentUser.id).filter(u => {
        if (currentUser.role === 'student') return u.role === 'teacher' || u.role === 'master';
        return true; 
    });

    const fetchMsg = useCallback(async () => {
        if(!selectedUser) return;
        try {
            const { data } = await api.get(`/messages/${selectedUser.id}`);
            setMessages(data);
        } catch (e) { console.error(e); }
    }, [selectedUser]);

    useEffect(() => {
        fetchMsg();
        const interval = setInterval(fetchMsg, 3000);
        return () => clearInterval(interval);
    }, [fetchMsg]);

    useEffect(() => bottomRef.current?.scrollIntoView({behavior:'smooth'}), [messages]);

    const send = async (e) => {
        e.preventDefault();
        if(!input.trim() || !selectedUser) return;
        try { await api.post('/messages', { receiverId: selectedUser.id, content: input }); setInput(''); fetchMsg(); } catch (e) { alert("Failed"); }
    };

    return (
        <div className="mt-8 bg-white rounded-xl shadow border flex h-[450px] overflow-hidden">
            <div className="w-1/3 border-r bg-gray-50 overflow-y-auto">
                <div className="p-3 border-b text-xs font-bold text-gray-400 uppercase tracking-wider">Contacts</div>
                {chatContacts.map(u => (
                    <div key={u.id} onClick={()=>setSelectedUser(u)} className={`p-4 border-b cursor-pointer hover:bg-blue-50 ${selectedUser?.id === u.id ? 'bg-white border-l-4 border-blue-500 shadow-inner' : ''}`}>
                        <p className="font-bold text-sm">{u.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{u.role}</p>
                    </div>
                ))}
            </div>
            <div className="w-2/3 flex flex-col bg-slate-50">
                {selectedUser ? (
                    <>
                        <div className="p-4 border-b font-bold bg-white text-gray-800">{selectedUser.name}</div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {messages.map(m => (
                                <div key={m.id} className={`flex ${m.senderId === currentUser.id ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`p-3 rounded-lg text-sm max-w-[80%] ${m.senderId === currentUser.id ? 'bg-blue-600 text-white' : 'bg-white border text-gray-800 shadow-sm'}`}>{m.content}</div>
                                </div>
                            ))}
                            <div ref={bottomRef}/>
                        </div>
                        <form onSubmit={send} className="p-3 border-t bg-white flex gap-2">
                            <input className="flex-1 border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={input} onChange={e=>setInput(e.target.value)} placeholder="Type a message..." />
                            <button className="bg-blue-600 text-white px-4 rounded-lg shadow-md transition-colors hover:bg-blue-700"><FaPaperPlane/></button>
                        </form>
                    </>
                ) : <div className="flex-1 flex items-center justify-center text-gray-400">Select a contact to chat</div>}
            </div>
        </div>
    );
};

// --- MAIN PAGE ---
const DashboardPage = () => {
    const { user, logout } = useAuth();
    const { navigateTo } = useNavigation();
    const [allUsers, setAllUsers] = useState([]); // Initialize as empty array
    const [allClasses, setAllClasses] = useState([]);
    const [modal, setModal] = useState(null);
    const [targetUser, setTargetUser] = useState(null);
    const [creditUpdate, setCreditUpdate] = useState(0);

    const role = user?.role ? user.role.toLowerCase() : '';
    const isMaster = role === 'master' || user?.username === 'theworldplex';
    const isTeacher = role === 'teacher';
    const isStudent = role === 'student';

    const fetchData = useCallback(async () => {
        if (!user) return;

        // Only attempt to fetch users if you are NOT a student
        if (user.role === 'master' || user.role === 'teacher' || user.username === 'theworldplex') {
            try { 
                const uRes = await api.get('/users'); 
                setAllUsers(uRes.data); 
            } catch(e) { console.error("Error fetching users:", e); }
        }

        try {
            const cRes = await api.get('/classes');
            setAllClasses(cRes.data);
        } catch (e) {
            console.log("Could not fetch classes");
        }
    }, [user]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUpdateCredits = async (userId, newCredits) => {
        try { await api.put(`/users/${userId}`, { lessonCredits: newCredits }); fetchData(); setModal(null); } 
        catch(e) { alert("Update failed"); }
    };

    const handleSaveNotes = async (userId, notesJson) => {
        try { await api.put(`/users/${userId}`, { notes: notesJson }); fetchData(); } 
        catch(e) { alert("Failed to save notes"); }
    };

    // --- FIX: WORKAROUND - Save to 'allowedCourses' ---
    const handleSaveAccess = async (userId, coursesString, assignedClass, assignedLesson) => {
        try { 
            // We use 'allowedCourses' because we know the DB column exists
            // We also send 'enrolledCourses' just in case the backend was updated partially
            await api.put(`/users/${userId}`, { 
                allowedCourses: coursesString, 
                enrolledCourses: coursesString,
                assignedClass, 
                assignedLesson 
            }); 
            fetchData(); 
        } 
        catch(e) { alert("Failed to save access permissions"); }
    };

    const deleteUser = async (id) => {
        if(!window.confirm("Delete this user?")) return;
        try { await api.delete(`/users/${id}`); fetchData(); } 
        catch (e) { alert("Failed to delete user"); }
    };

    if (!user) return null;

    // --- HELPER: Read student data locally ---
    const getEnrolledLabels = () => {
        // Read from allowedCourses first (legacy data priority)
        const rawCourses = user.allowedCourses || user.enrolledCourses;
        let enrolledIds = [];
        
        if (Array.isArray(rawCourses)) {
            enrolledIds = rawCourses;
        } else if (typeof rawCourses === 'string') {
            try { 
                let clean = rawCourses;
                if (clean.startsWith('"') && clean.endsWith('"')) clean = clean.slice(1, -1);
                enrolledIds = JSON.parse(clean);
            } catch { enrolledIds = []; }
        }

        if (!Array.isArray(enrolledIds)) return [];
        return ALL_COURSES.filter(c => enrolledIds.includes(c.id));
    };

    return (
        <PageLayout>
            <div className="min-h-screen overflow-y-auto bg-gray-50 pb-20">
                <div className="container mx-auto p-6 max-w-7xl">
                    <div className="bg-white rounded-xl shadow p-6 mb-6 flex justify-between items-center border border-gray-100">
                        <div>
                            <h1 className="text-3xl font-black text-gray-800 capitalize">{user.role} Dashboard</h1>
                            <p className="text-gray-500 font-medium">Welcome back, {user.name}</p>
                            {isStudent && (
                                <div className="mt-2 flex gap-3">
                                    <div className="inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-bold text-sm flex items-center gap-1">
                                        <FaCoins /> Credits: {user.lessonCredits || 0}
                                    </div>
                                    {user.assignedClass && (
                                        <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full font-bold text-sm flex items-center gap-1">
                                            <FaUsers /> {user.assignedClass}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        <button onClick={logout} className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-md transition-transform active:scale-95 text-lg">
                            <FaSignOutAlt /> Log Out
                        </button>
                    </div>

                    {isStudent && (user.assignedLesson || getEnrolledLabels().length > 0) && (
                        <div className="mb-8">
                             <h2 className="text-2xl font-black text-gray-800 mb-4 border-b pb-2 flex items-center gap-2">
                                <FaClipboardList className="text-blue-600" /> Your Assignments
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                {user.assignedLesson && (
                                    <div onClick={()=>navigateTo('eslCourses')} className="bg-gradient-to-r from-orange-100 to-orange-50 border-l-8 border-orange-500 p-6 rounded-xl shadow-md cursor-pointer hover:shadow-lg transition-all">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-1">Teacher Assigned</p>
                                                <h3 className="text-xl font-bold text-gray-800">{user.assignedLesson}</h3>
                                                <p className="text-sm text-gray-600 mt-2">Click to browse to this lesson and start working.</p>
                                            </div>
                                            <FaStar className="text-orange-400 text-4xl" />
                                        </div>
                                        <button className="mt-4 w-full bg-orange-500 text-white font-bold py-2 rounded-lg hover:bg-orange-600">Go to Lessons</button>
                                    </div>
                                )}
                                <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Unlocked Courses</p>
                                    <div className="space-y-2">
                                        {getEnrolledLabels().length > 0 ? getEnrolledLabels().map(c => (
                                            <div key={c.id} className="flex items-center gap-2 text-green-700 font-bold">
                                                <FaCheckSquare /> {c.label}
                                            </div>
                                        )) : <p className="text-gray-400 italic">No courses unlocked yet.</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <button onClick={()=>navigateTo('eslCourses')} className="bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-xl font-bold shadow-lg flex flex-col items-center gap-2 transition hover:-translate-y-1">
                            <FaBookOpen /> Browse Lessons
                        </button>
                        {isMaster && (
                            <>
                                <button onClick={()=>setModal('addStudent')} className="bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold shadow-lg flex flex-col items-center gap-2 transition hover:-translate-y-1"><FaUserPlus/> Add Student</button>
                                <button onClick={()=>setModal('addTeacher')} className="bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-xl font-bold shadow-lg flex flex-col items-center gap-2 transition hover:-translate-y-1"><FaUserPlus/> Add Teacher</button>
                            </>
                        )}
                        {(isMaster || isTeacher) && (
                            <button onClick={()=>navigateTo('createClass')} className="bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-bold shadow-lg flex flex-col items-center gap-2 transition hover:-translate-y-1"><FaChalkboardTeacher/> Create Class</button>
                        )}
                    </div>

                    {(isMaster || isTeacher) && (
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-8">
                            <h2 className="text-xl font-black mb-6 border-b pb-4 text-gray-800">
                                {isMaster ? "User Management" : "My Students"}
                            </h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {allUsers
                                    .filter(u => u.id !== user.id)
                                    .filter(u => isMaster ? true : (u.role && u.role.toLowerCase() === 'student'))
                                    .map(u => (
                                    <div key={u.id} className="border border-gray-200 rounded-xl p-5 bg-white hover:shadow-xl transition-shadow relative">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <p className="font-bold text-lg text-gray-800">{u.name}</p>
                                                <p className="text-xs text-gray-500 font-medium">@{u.username} • <span className="uppercase text-blue-600">{u.role}</span></p>
                                                {u.assignedClass && <p className="text-xs text-green-600 font-bold mt-1">Class: {u.assignedClass}</p>}
                                            </div>
                                            {isMaster && (
                                                <div className="flex gap-2">
                                                    <button onClick={() => { setTargetUser(u); setModal('editUser'); }} className="text-orange-400 hover:text-orange-600"><FaEdit /></button>
                                                    <button onClick={() => deleteUser(u.id)} className="text-gray-300 hover:text-red-500"><FaTrash /></button>
                                                </div>
                                            )}
                                        </div>
                                        {(u.role && u.role.toLowerCase() === 'student') && (
                                            <div className="space-y-2">
                                                <div className="flex gap-2">
                                                    <button onClick={() => { setTargetUser(u); setCreditUpdate(u.lessonCredits || 0); setModal('editCredits'); }} className="flex-1 text-sm bg-yellow-100 text-yellow-800 py-2 rounded-lg font-bold shadow-sm hover:bg-yellow-200 border border-yellow-200 flex items-center justify-center gap-2">
                                                        {u.lessonCredits || 0} <FaCoins /> Credits
                                                    </button>
                                                    <button onClick={() => { setTargetUser(u); setModal('notes'); }} className="flex-1 text-sm bg-purple-100 text-purple-800 py-2 rounded-lg font-bold shadow-sm hover:bg-purple-200 border border-purple-200 flex items-center justify-center gap-2">
                                                        <FaStickyNote /> Notes
                                                    </button>
                                                </div>
                                                <button onClick={() => { setTargetUser(u); setModal('access'); }} className="w-full text-sm bg-green-100 text-green-800 py-2 rounded-lg font-bold shadow-sm hover:bg-green-200 border border-green-200 flex items-center justify-center gap-2">
                                                    <FaUnlockAlt /> Manage Access & Class
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {allUsers.length === 0 && <p className="text-gray-400">No users found.</p>}
                            </div>
                        </div>
                    )}

                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        <h2 className="text-2xl font-black text-gray-800 mb-4 border-b pb-2">Communication Center</h2>
                        <DashboardChat currentUser={user} allUsers={allUsers} />
                    </div>
                </div>
            </div>

            {/* Modals */}
            {modal === 'addStudent' && <AddUserModal isOpen={true} onClose={()=>setModal(null)} onUserAdded={()=>{setModal(null); fetchData();}} role="student" />}
            {modal === 'addTeacher' && <AddUserModal isOpen={true} onClose={()=>setModal(null)} onUserAdded={()=>{setModal(null); fetchData();}} role="teacher" />}
            {modal === 'editUser' && targetUser && <EditUserModal isOpen={true} user={targetUser} onClose={() => setModal(null)} onUserUpdated={() => { setModal(null); fetchData(); }} />}
            {modal === 'notes' && targetUser && <StudentNotesModal student={targetUser} onClose={() => setModal(null)} onSave={handleSaveNotes} />}
            {modal === 'access' && targetUser && <CourseAccessModal student={targetUser} classes={allClasses} onClose={() => setModal(null)} onSave={handleSaveAccess} />}
            {modal === 'editCredits' && targetUser && (
                <Modal title={`Credits: ${targetUser.name}`} onClose={()=>setModal(null)}>
                    <div className="text-center space-y-8 p-4">
                         <div className="flex items-center justify-center gap-6">
                            <button onClick={() => setCreditUpdate(prev => Math.max(0, prev - 1))} className="w-16 h-16 rounded-full bg-red-100 text-red-600 font-black text-3xl hover:bg-red-200 transition">-</button>
                            <input type="number" min="0" value={creditUpdate} onChange={(e) => setCreditUpdate(Math.max(0, parseInt(e.target.value) || 0))} className="text-6xl font-black text-gray-800 w-48 text-center border-b-4 border-gray-200 focus:border-blue-500 outline-none bg-transparent" />
                            <button onClick={() => setCreditUpdate(prev => prev + 1)} className="w-16 h-16 rounded-full bg-green-100 text-green-600 font-black text-3xl hover:bg-green-200 transition">+</button>
                        </div>
                        <button onClick={() => handleUpdateCredits(targetUser.id, creditUpdate)} className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-blue-700 transition transform active:scale-95">Save Credits</button>
                    </div>
                </Modal>
            )}
        </PageLayout>
    );
};

export default DashboardPage;